import { match } from 'ts-pattern'

import {
  isEnrichedTextArray,
  isListBlockConfigV3,
  isTableBlockConfig,
  isTextBlockConfig,
  QuoteVariableType
} from '@sequencehq/quote-content'

import {
  getVariableLabel,
  getVariableValue
} from 'Cube/view/layouts/quote/QuoteEditorContent/utils/quoteVariables'
import {
  CubeReducerState,
  ValidationResult
} from 'modules/Cube/domain/cube.domain.types'

export const noUnassignedQuoteVariables = (
  prevState: CubeReducerState
): ValidationResult[] | null => {
  const allEnrichedText = prevState.data.presentation.blocks.flatMap(block => {
    return match(block)
      .with(
        { blockType: 'heading1' },
        { blockType: 'heading2' },
        { blockType: 'heading3' },
        { blockType: 'paragraph' },
        () => {
          if (!isTextBlockConfig(block.config)) return []
          if (!isEnrichedTextArray(block.config.text)) return []
          return block.config.text
        }
      )
      .with({ blockType: 'bulletList' }, { blockType: 'numberList' }, () => {
        if (!isListBlockConfigV3(block.config)) return []

        return block.config.items.flatMap(item => {
          if (!isEnrichedTextArray(item.text)) return []
          return item.text
        })
      })
      .with({ blockType: 'table' }, () => {
        if (!isTableBlockConfig(block.config)) return []
        return block.config.rows.flatMap(row =>
          row.cells.flatMap(cell => cell.text)
        )
      })
      .with(
        { blockType: 'coverImage' },
        { blockType: 'pdfCoverImage' },
        { blockType: 'image' },
        { blockType: 'phases' },
        { blockType: 'divider' },
        () => []
      )
      .exhaustive()
  })

  const allVariablesInUse = Array.from(
    new Set([
      ...allEnrichedText
        .filter(text => !!text.variable)
        .map(text => text.variable)
    ])
  ) as QuoteVariableType[]

  if (!allVariablesInUse.length) return null

  const unsetVariables = allVariablesInUse.filter(variableType => {
    return !getVariableValue(prevState.queries, prevState.data)(variableType)
  })

  if (!unsetVariables.length) return null

  const message = (() => {
    const variableLabels = unsetVariables.map((variableType, index) => {
      const label = getVariableLabel(variableType)
      return index === 0 ? label : label.toLowerCase()
    })

    if (variableLabels.length === 1) {
      return `${variableLabels[0]} variable is unset`
    }

    if (variableLabels.length === 2) {
      return `${variableLabels[0]} and ${variableLabels[1]} variables are unset`
    }

    const allButLast = variableLabels.slice(0, -1)
    const lastVariable = variableLabels.at(-1)
    return `${allButLast.join(', ')} and ${lastVariable} variables are unset`
  })()

  return [{ message }]
}
